import React, {ReactElement, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {FormProvider, useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {IADNumberFormatting, TrunkingSite} from '../../app/types'
import ErrorAlert from '../../common/components/ErrorAlert'
import FormContainer from '../../common/form/FormContainer'
import FormRow from '../../common/form/FormRow'
import FormFieldBox from '../../common/form/FormFieldBox'
import TextInput from '../../common/form/TextInput'
import {useFormSubmission} from '../../common/servercall/useFormSubmission'
import FormGroup from '../../common/form/FormGroup'
import {formatCallingPhoneNumber} from "../../common/util/formatters";

const schema = yup
    .object()
    .shape({

    }).required()

type Props = {
    form: IADNumberFormatting
    onSubmit: (formData: IADNumberFormatting) => void
    buildFormButtons: (isSubmitting: boolean) => ReactElement
    trunkingSite: TrunkingSite | undefined
}

const SoiPeeringNumberFormattingFormScreen = ({form, onSubmit, buildFormButtons, trunkingSite }: Props) => {
    const { t } = useTranslation()

    const { control, handleSubmit, setError, clearErrors } = useForm<IADNumberFormatting>({
        resolver: yupResolver(schema),
        values: form,
    })

    const nationalCalledOptions =  useMemo(
        () =>
            ['PLUS_E164', 'ZERO_ZERO_E164', 'E164', 'ZERO_NAT', 'NAT'].map(formatting => ({
                value: `${formatting}`,
                label: `${t(`trunking-sites.calling-number-format.${formatting}`, {number: formatCallingPhoneNumber(trunkingSite?.mainNumber || '', formatting)})}`,
            })),
        [t, trunkingSite]
    )

    const nationalCallingOptions =  useMemo(
        () =>
            ['PLUS_E164', 'ZERO_ZERO_E164','ZERO_NAT'].map(formatting => ({
                value: `${formatting}`,
                label: `${t(`trunking-sites.calling-number-format.${formatting}`, {number: formatCallingPhoneNumber('+3215666666', formatting)})}`,
            })),
        [t]
    )

    const internationalCallingOptions =  useMemo(
        () =>
            ['PLUS_E164', 'ZERO_ZERO_E164'].map(formatting => ({
                value: `${formatting}`,
                label: `${t(`trunking-sites.calling-number-format.${formatting}`, {number: formatCallingPhoneNumber('+17182222222', formatting)})}`,
            })),
        [t]
    )

    const { submit, isSubmitting, serverError, onError } = useFormSubmission<IADNumberFormatting>(onSubmit, setError)

    return (
        // @ts-ignore
        <FormProvider clearErrors={clearErrors}>
            <ErrorAlert errorKey={typeof serverError === 'string' ? serverError : undefined} showAlert={!!serverError} />
            <FormContainer onSubmit={handleSubmit(submit, onError)}>
                <FormGroup fullWidth={true}>
                    <FormRow>
                        <FormFieldBox >
                            <TextInput
                                readonly={false}
                                label={t('trunking-sites.national-called')}
                                name={'nationalCalled'}
                                control={control}
                                required={true}
                                options={nationalCalledOptions}
                            />
                        </FormFieldBox>
                    </FormRow>
                    <FormRow>
                        <FormFieldBox >
                            <TextInput
                                readonly={false}
                                label={t('trunking-sites.national-calling')}
                                name={'nationalCalling'}
                                control={control}
                                required={true}
                                options={nationalCallingOptions}
                            />
                        </FormFieldBox>
                    </FormRow>
                    <FormRow>
                        <FormFieldBox >
                            <TextInput
                                readonly={false}
                                label={t('trunking-sites.international-calling')}
                                name={'internationalCalling'}
                                control={control}
                                options={internationalCallingOptions}
                                required={true}
                            />
                        </FormFieldBox>
                    </FormRow>
                </FormGroup>
                {buildFormButtons(isSubmitting)}
            </FormContainer>
        </FormProvider>
    )
}

export default SoiPeeringNumberFormattingFormScreen
